import { useCallback, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

export type GovernanceStage =
  | 'idle'
  | 'company_extraction'
  | 'dimension_extraction'
  | 'dimension_merge'
  | 'priority_filter'
  | 'dimension_comparison'
  | 'question_merge'
  | 'document_resolution'
  | 're_extraction'
  | 'awaiting_hitl'
  | 'dimension_analysis'
  | 'score_aggregation'
  | 'done'
  | 'error';

export interface AreaProcessed {
  area: string;
  cached: boolean;
}

export interface StepperNode {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'complete';
}

export interface GovernanceQuestion {
  question_id: string;
  dimension_id: string;
  policy_id: string;
  element_id?: string;
  question_text: string;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
}

export interface DimensionResult {
  dimension_id: string;
  status: string;
  evidence_used: string[];
  gap: string | null;
  severity: string | null;
  score_contribution: number;
  recommendation: string | null;
  question?: string;
  area?: string;
  // Enriched fields from _analyse_dimension
  policy_reference?: string | null;
  company_evidence_summary?: string | null;
  reasoning?: string | null;
  source_policy_id?: string | null;
  source_policy_name?: string | null;
  doc_sources?: string[];
}

export interface AreaScore {
  score: number;
  coverage: number;
  dimensions_met: number;
  dimensions_partial: number;
  dimensions_missing: number;
}

export interface PolicyCoverage {
  raw_dimensions: number;
  covered: number;
  met: number;
  partial: number;
  missing: number;
}

export interface DeferredSummary {
  total: number;
  by_area: Record<string, number>;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  per_area_counts: Record<string, number>;
  deferred_by_area?: Record<string, number>;
  from_cache?: boolean;
}

export interface ResolutionStats {
  total_raw_questions: number;
  resolved_by_existing_docs: number;
  resolved_by_uploaded_docs: number;
  resolved_by_text_answers: number;
  document_requests_sent: number;
  standalone_questions_sent: number;
}

export interface GovernanceFinalResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_score: number;
  health_rating: string;
  area_scores: Record<string, AreaScore>;
  dimension_results: DimensionResult[];
  critical_gaps: (DimensionResult & { question: string; area: string })[];
  high_gaps: (DimensionResult & { question: string; area: string })[];
  top_recommendations: { recommendation: string; severity: string | null; dimension_id: string; question: string }[];
  evidence_map: Record<string, { question: string; evidence_used: string[]; doc_sources: string[] }>;
  assessed_at: string;
  hitl_iterations: number;
  dimensions_from_cache: number;
  areas_from_cache: number;
  total_elements: number;
  elements_met: number;
  elements_partial: number;
  elements_missing: number;
  coverage_score: number;
  ownership_score: number;
  policy_coverage?: Record<string, PolicyCoverage>;
  deferred_summary?: DeferredSummary;
  merge_stats?: MergeStats;
  resolution_stats?: ResolutionStats;
}

export interface NodeStat {
  done: number;
  total: number;
  extra?: Record<string, number>;
}

export interface GovernanceSocketState {
  stage: GovernanceStage;
  stageMessage: string;
  sessionId: string;
  stepperNodes: StepperNode[];
  areasProcessed: AreaProcessed[];
  dimensionsExtracted: number;
  comparisonProgress: string;
  totalDimensions: number;
  questionsCount: number;
  questions: GovernanceQuestion[];
  finalResult: GovernanceFinalResult | null;
  error: string | null;
  eventLog: string[];
  nodeStats: Record<string, NodeStat>;
}

const DEFAULT_STEPPER: StepperNode[] = [
  { id: 'company_extraction', label: 'Extracting company governance evidence', status: 'pending' },
  { id: 'dimension_extraction', label: 'Extracting policy dimensions', status: 'pending' },
  { id: 'dimension_merge', label: 'Deduplicating dimensions', status: 'pending' },
  { id: 'priority_filter', label: 'Filtering by priority', status: 'pending' },
  { id: 'dimension_comparison', label: 'Running comparisons', status: 'pending' },
  { id: 'question_merge', label: 'Consolidating questions', status: 'pending' },
  { id: 'document_resolution', label: 'Resolving via documents', status: 'pending' },
  { id: 're_extraction', label: 'Auto-extracting answers', status: 'pending' },
  { id: 'hitl', label: 'Awaiting questionnaire', status: 'pending' },
  { id: 'dimension_analysis', label: 'Analysing results', status: 'pending' },
  { id: 'score_aggregation', label: 'Calculating scores', status: 'pending' },
];

const INITIAL_STATE: GovernanceSocketState = {
  stage: 'idle',
  stageMessage: '',
  sessionId: '',
  stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })),
  areasProcessed: [],
  dimensionsExtracted: 0,
  comparisonProgress: '',
  totalDimensions: 0,
  questionsCount: 0,
  questions: [],
  finalResult: null,
  error: null,
  eventLog: [],
  nodeStats: {},
};

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const WS_BASE = API_BASE.replace(/^http/, 'ws');
const MAX_LOG = 5;

const updateStepper = (nodes: StepperNode[], nodeId: string, status: 'running' | 'complete'): StepperNode[] =>
  nodes.map(n => n.id === nodeId ? { ...n, status } : n);

const addLog = (logs: string[], msg: string): string[] =>
  [...logs.slice(-(MAX_LOG - 1)), msg];

const handleMessage = (msg: any, prev: GovernanceSocketState): GovernanceSocketState => {
  const event = msg.event as string;

  switch (event) {
    case 'session':
      return { ...prev, sessionId: msg.session_id };

    case 'node_started': {
      const nodeId = msg.node as string;
      const stageMap: Record<string, GovernanceStage> = {
        company_extraction: 'company_extraction',
        dimension_extraction: 'dimension_extraction',
        dimension_merge: 'dimension_merge',
        priority_filter: 'priority_filter',
        dimension_comparison: 'dimension_comparison',
        question_merge: 'question_merge',
        document_resolution: 'document_resolution',
        re_extraction: 're_extraction',
        dimension_analysis: 'dimension_analysis',
        score_aggregation: 'score_aggregation',
      };
      return {
        ...prev,
        stage: stageMap[nodeId] ?? prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updateStepper(prev.stepperNodes, nodeId, 'running'),
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} started`),
      };
    }

    case 'node_complete': {
      const nodeId = msg.node as string;
      const updated = updateStepper(prev.stepperNodes, nodeId, 'complete');
      const isDone = nodeId === 'score_aggregation';
      const newStats = { ...prev.nodeStats };
      if (typeof msg.done === 'number') {
        newStats[nodeId] = { done: msg.done, total: msg.total ?? msg.done, extra: msg.extra };
      }
      return {
        ...prev,
        stage: isDone ? 'done' : prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updated,
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} complete`),
        nodeStats: newStats,
      };
    }

    case 'area_processed':
      return {
        ...prev,
        areasProcessed: [...prev.areasProcessed, { area: msg.area, cached: msg.cached }],
        eventLog: addLog(prev.eventLog, `Area: ${msg.area} ${msg.cached ? '(cached)' : ''}`),
      };

    case 'dimension_extracted':
      return {
        ...prev,
        dimensionsExtracted: prev.dimensionsExtracted + (msg.count ?? 0),
        eventLog: addLog(prev.eventLog, `Extracted ${msg.count} dimensions from ${msg.policy_id?.slice(0, 8)}...`),
      };

    case 'comparison_started':
      return { ...prev, totalDimensions: msg.total_dimensions ?? 0 };

    case 'dimension_compared':
      return {
        ...prev,
        comparisonProgress: msg.progress ?? '',
        eventLog: addLog(prev.eventLog, `Compared ${msg.progress}: ${msg.matched ? 'matched' : 'gap found'}`),
      };

    case 'questions_ready':
      return {
        ...prev,
        stage: 'awaiting_hitl',
        questionsCount: msg.count ?? 0,
        stepperNodes: updateStepper(prev.stepperNodes, 'hitl', 'running'),
        eventLog: addLog(prev.eventLog, `${msg.count} questions need your input`),
      };

    case 'error':
      return {
        ...prev,
        stage: 'error',
        error: msg.message ?? 'Unknown error',
        eventLog: addLog(prev.eventLog, `Error: ${msg.message}`),
      };

    default:
      return prev;
  }
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useGovernanceSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const [state, setState] = useState<GovernanceSocketState>({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });

  const reset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
  }, []);

  const connectWS = useCallback((url: string, onOpen?: (ws: WebSocket) => void) => {
    wsRef.current?.close();

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectCountRef.current = 0;
      onOpen?.(ws);
    };

    ws.onmessage = (ev: MessageEvent) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      setState(prev => handleMessage(msg, prev));
    };

    ws.onerror = () => {
      setState(prev => {
        // If a session is already underway, ignore the WS error silently —
        // the HTTP polling fallback will detect completion when the tab refocuses.
        if (prev.sessionId) return prev;
        return { ...prev, stage: 'error', error: 'WebSocket connection failed.' };
      });
    };

    ws.onclose = () => {
      wsRef.current = null;
    };
  }, []);

  const start = useCallback((companyId: string, policyIds: string[], token: string, jurisdiction?: string) => {
    setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
    const url = `${WS_BASE}/api/v1/governance/ws?token=${encodeURIComponent(token)}`;
    connectWS(url, (ws) => {
      ws.send(JSON.stringify({ company_id: companyId, policy_ids: policyIds, jurisdiction: jurisdiction || '' }));
    });
  }, [connectWS]);

  const resume = useCallback((sessionId: string, token: string) => {
    const url = `${WS_BASE}/api/v1/governance/ws/resume?token=${encodeURIComponent(token)}`;
    setState(prev => ({
      ...prev,
      stage: 'dimension_analysis',
      stageMessage: 'Resuming assessment with your answers...',
      stepperNodes: updateStepper(
        updateStepper(prev.stepperNodes, 'hitl', 'complete'),
        'dimension_analysis', 'running',
      ),
    }));
    connectWS(url, (ws) => {
      ws.send(JSON.stringify({ session_id: sessionId }));
    });
  }, [connectWS]);

  const setQuestions = useCallback((questions: GovernanceQuestion[]) => {
    setState(prev => ({ ...prev, questions }));
  }, []);

  const setFinalResult = useCallback((result: GovernanceFinalResult) => {
    setState(prev => ({ ...prev, finalResult: result }));
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { state, start, resume, reset, disconnect, setQuestions, setFinalResult };
}
